import { useCallback } from 'react';
import type { InfiniteData, QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import type { DocumentsApi, SharedApi } from '@izimi/api';
import { ApiDocumentSource } from '@izimi/api';
import type { FirstParameter } from '@izimi/utils';
import { chunkArray } from '@izimi/utils';

import { isCommunicationDetails, isDocument } from './helpers';
import SharedQueryKeys from './SharedQueryKeys';

export const sharedQueryKeys = new SharedQueryKeys();

type PaginatedSharedItemsQueryData = InfiniteData<
  Awaited<ReturnType<typeof SharedApi.prototype.getSharedItems>>,
  unknown
>;
type PaginatedSharedItemsQueriesData = Array<[QueryKey, PaginatedSharedItemsQueryData | undefined]>;
type SharedItemsDocumentsCopyParams = { communicationId: string; documentIds: Array<string> };

export const useInvalidateSharedItemsCache = () => {
  const queryClient = useQueryClient();

  const invalidateSharedItemsCache = useCallback(
    () =>
      Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: sharedQueryKeys.getLastSharedItems(),
        }),
        queryClient.invalidateQueries({
          queryKey: sharedQueryKeys.getPagedSharedItems(),
        }),
      ]),
    [queryClient],
  );

  return invalidateSharedItemsCache;
};

export const useOptimisticSharedItemsDocumentsCopyCacheUpdate = () => {
  const queryClient = useQueryClient();

  const onMutate = useCallback(
    async ({ communicationId, documentIds }: SharedItemsDocumentsCopyParams) => {
      await queryClient.cancelQueries({ queryKey: sharedQueryKeys.getPagedSharedItems() });
      const previousSharedItemsQueries: PaginatedSharedItemsQueriesData =
        queryClient.getQueriesData({ queryKey: sharedQueryKeys.getPagedSharedItems() });

      previousSharedItemsQueries?.forEach(([queryKey, data]) => {
        if (!data?.pages) return;

        const updatedData = {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((item) => {
              if (
                isCommunicationDetails(item) &&
                item.id === communicationId &&
                item.type === 'document'
              ) {
                return {
                  ...item,
                  documents: item.documents.map((document) => {
                    if (documentIds.includes(document.documentId)) {
                      return { ...document, isAvailable: false };
                    }

                    return document;
                  }),
                };
              }

              return item;
            }),
          })),
        };
        queryClient.setQueryData(queryKey, updatedData);
      });

      return { previousSharedItemsQueries };
    },
    [queryClient],
  );

  const onError = useCallback(
    async (
      _: unknown,
      __: SharedItemsDocumentsCopyParams,
      context: { previousSharedItemsQueries: PaginatedSharedItemsQueriesData } | undefined,
    ) => {
      if (context?.previousSharedItemsQueries) {
        context.previousSharedItemsQueries.forEach(([queryKey, data]) => {
          if (!data) return;

          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    [queryClient],
  );

  return {
    onMutate,
    onError,
  };
};

export const useOptimisticSharedItemsCommunicationSeenCacheUpdate = () => {
  const queryClient = useQueryClient();

  const markSharedItemCommunicationAsSeen = useCallback(
    async ({ communicationId }: { communicationId: string }) => {
      const previousSharedItemsQueries: PaginatedSharedItemsQueriesData =
        queryClient.getQueriesData({ queryKey: sharedQueryKeys.getPagedSharedItems() });

      previousSharedItemsQueries?.forEach(([queryKey, data]) => {
        if (!data) return;

        let updatedUnseenCommunication = 0;
        let updatedExpiringUnseenCommunication = 0;

        const updatedData = {
          ...data,
          pages: data.pages
            .map((page) => ({
              ...page,
              items: page.items.map((item) => {
                if (isCommunicationDetails(item) && item.id === communicationId) {
                  if (item.isSeenReceiver) {
                    return item;
                  }

                  if (item._collection === 'EXPIRING_COMMUNICATION_DETAILS') {
                    updatedExpiringUnseenCommunication += 1;
                  }

                  if (item._collection === 'NOT_EXPIRING_COMMUNICATION_DETAILS') {
                    updatedUnseenCommunication += 1;
                  }

                  return {
                    ...item,
                    isSeenReceiver: true,
                  };
                }

                return item;
              }),
            }))
            .map((page) => ({
              ...page,
              metadata: {
                ...page.metadata,
                ...(page.metadata?.numberUnseenCommunications && {
                  numberUnseenCommunications:
                    (page.metadata.numberUnseenCommunications ?? 0) - updatedUnseenCommunication,
                }),
                ...(page.metadata?.numberUnseenExpiringCommunications && {
                  numberUnseenExpiringCommunications:
                    (page.metadata.numberUnseenExpiringCommunications ?? 0) -
                    updatedExpiringUnseenCommunication,
                }),
              },
            })),
        };

        if (updatedUnseenCommunication === 0 && updatedExpiringUnseenCommunication === 0) {
          return;
        }

        queryClient.setQueryData(queryKey, updatedData);
      });

      return { previousSharedItemsQueries };
    },
    [queryClient],
  );

  return {
    markSharedItemCommunicationAsSeen,
  };
};

export const useOptimisticSharedItemsDocumentAcceptRejectCacheUpdate = () => {
  const queryClient = useQueryClient();

  const onMutate = useCallback(
    async ({
      acceptedDocuments,
      rejectedDocuments,
    }: FirstParameter<DocumentsApi['acceptRejectSharedDocuments']>) => {
      await queryClient.cancelQueries({ queryKey: sharedQueryKeys.getPagedSharedItems() });
      const previousSharedItemsQueries: PaginatedSharedItemsQueriesData =
        queryClient.getQueriesData({ queryKey: sharedQueryKeys.getPagedSharedItems() });

      const processedDocumentIds = [
        ...(acceptedDocuments ?? []).map((document) => document.documentId),
        ...(rejectedDocuments ?? []).map((document) => document.documentId),
      ];

      previousSharedItemsQueries?.forEach(([queryKey, data]) => {
        if (!data) return;

        let removedItemsCount = 0;

        const items = data.pages
          .flatMap((page) => page.items)
          .filter((item) => {
            const shouldKeep =
              !isDocument(item) ||
              item.source === ApiDocumentSource.Shared ||
              !processedDocumentIds.includes(item.id);
            if (!shouldKeep) {
              removedItemsCount += 1;
            }
            return shouldKeep;
          })
          .map((item) => {
            if (!processedDocumentIds.includes(item.id) || !isDocument(item)) {
              return item;
            }
            return {
              ...item,
              state: 'ACTIVE',
            };
          });

        if (items.length === 0) {
          queryClient.setQueryData(queryKey, {
            pageParams: [],
            pages: [
              {
                items: [],
                total: 0,
              },
            ],
          });
          return;
        }

        queryClient.setQueryData(queryKey, {
          pageParams: data?.pageParams,
          pages: chunkArray(items, 15).map((items) => ({
            ...data.pages[0],
            items,
            total: (data.pages[0]?.total ?? 0) - removedItemsCount,
          })),
        });
      });

      return { previousSharedItemsQueries };
    },
    [queryClient],
  );

  const onError = useCallback(
    async (
      _: unknown,
      __: unknown,
      context: { previousSharedItemsQueries: PaginatedSharedItemsQueriesData } | undefined,
    ) => {
      if (context?.previousSharedItemsQueries) {
        context.previousSharedItemsQueries.forEach(([queryKey, data]) => {
          if (!data) return;

          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    [queryClient],
  );

  return {
    onMutate,
    onError,
  };
};

export const useOptimisticSharedItemsCommunicationsDeleteCacheUpdate = () => {
  const queryClient = useQueryClient();

  const onMutate = useCallback(
    async ({ communicationIds }: { communicationIds: Array<string> }) => {
      await queryClient.cancelQueries({ queryKey: sharedQueryKeys.getPagedSharedItems() });
      const previousSharedItemsQueries: PaginatedSharedItemsQueriesData =
        queryClient.getQueriesData({ queryKey: sharedQueryKeys.getPagedSharedItems() });

      previousSharedItemsQueries?.forEach(([queryKey, data]) => {
        if (!data) return;

        let removedItemsCount = 0;
        let removedUnseenCommunicationCount = 0;
        let removedExpiringUnseenCommunicationCount = 0;

        const items = data.pages
          .flatMap((page) => page.items)
          .filter((item) => {
            if (!isCommunicationDetails(item) || !communicationIds.includes(item.id)) {
              return true;
            }

            if (item._collection === 'NOT_EXPIRING_COMMUNICATION_DETAILS') {
              removedUnseenCommunicationCount += 1;
            }

            if (item._collection === 'EXPIRING_COMMUNICATION_DETAILS') {
              removedExpiringUnseenCommunicationCount += 1;
            }

            removedItemsCount += 1;

            return false;
          });

        if (items.length === 0) {
          queryClient.setQueryData(queryKey, {
            pageParams: [],
            pages: [
              {
                items: [],
                total: 0,
              },
            ],
          });
          return;
        }

        queryClient.setQueryData(queryKey, {
          pageParams: data?.pageParams,
          pages: chunkArray(items, 15).map((items) => ({
            ...data.pages[0],
            items,
            total: (data.pages[0]?.total ?? 0) - removedItemsCount,
            metadata: {
              ...data.pages[0]?.metadata,
              ...(data.pages[0]?.metadata?.numberUnseenCommunications && {
                numberUnseenCommunications:
                  (data.pages[0].metadata.numberUnseenCommunications ?? 0) -
                  removedUnseenCommunicationCount,
              }),
              ...(data.pages[0]?.metadata?.numberUnseenExpiringCommunications && {
                numberUnseenExpiringCommunications:
                  (data.pages[0].metadata.numberUnseenExpiringCommunications ?? 0) -
                  removedExpiringUnseenCommunicationCount,
              }),
            },
          })),
        });
      });

      return { previousSharedItemsQueries };
    },
    [queryClient],
  );

  const onError = useCallback(
    async (
      _: unknown,
      __: unknown,
      context: { previousSharedItemsQueries: PaginatedSharedItemsQueriesData } | undefined,
    ) => {
      if (context?.previousSharedItemsQueries) {
        context.previousSharedItemsQueries.forEach(([queryKey, data]) => {
          if (!data) return;

          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    [queryClient],
  );

  const onSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: sharedQueryKeys.getPagedSharedItems() });
  }, [queryClient]);

  return {
    onMutate,
    onError,
    onSuccess,
  };
};
